import { useState, type CSSProperties } from 'react'
import globe from '@/assets/images/globe.webp'
import { motion, AnimatePresence } from 'framer-motion'
import visibility from '@/assets/images/visibility.png'
import intelligence from '@/assets/images/intelligence.png'
import governance from '@/assets/images/governance.png'
import evolution from '@/assets/images/evolution.png'

const processItems = [
  { id: 'production', num: '01', label: 'Production' },
  { id: 'visibility', num: '02', label: 'Visibility' },
  { id: 'intelligence', num: '03', label: 'Intelligence' },
  { id: 'governance', num: '04', label: 'Governance' },
  { id: 'evolution', num: '05', label: 'Evolution' },
]

const details: Record<string, { title: string; description: string; image: string }> = {
  production: {
    title: 'Production',
    description:
      'Embedded teams executing technology initiatives, operations, and product delivery.',
    image: globe,
  },
  visibility: {
    title: 'Visibility',
    description:
      'Unified visibility across projects, infrastructure, systems, performance, and ongoing execution.',
    image: visibility,
  },
  intelligence: {
    title: 'Intelligence',
    description:
      'Centralized knowledge, documentation, decisions, and operational context management.',
    image: intelligence,
  },
  governance: {
    title: 'Governance',
    description:
      'Structured leadership, standards, accountability, and technical decision-making processes.',
    image: governance,
  },
  evolution: {
    title: 'Evolution',
    description: 'Continuous optimization, scaling, automation, and long-term technology growth.',
    image: evolution,
  },
}

export function BackofficeExecutionGrid() {
  const [active, setActive] = useState('production')
  const detail = details[active]

  return (
    <div className="mx-auto flex max-w-[1240px] flex-col overflow-hidden rounded-[10px] xl:h-[580px] xl:flex-row">
      {/* Left: nav list */}
      <motion.div
        className="w-full bg-[#FAFAFA] px-4 py-6 sm:px-6 sm:py-8 xl:h-[580px] xl:w-[401px] xl:px-[34px] xl:py-[49px]"
        initial={{ opacity: 0, x: -16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, ease: 'easeOut' }}
      >
        <p className="font-sans text-[15px] font-medium not-italic leading-normal tracking-[-1px] text-[#1C1917] sm:text-[18px] xl:text-[24px]">
          Technology Backoffice
        </p>

        <ul
          className="mt-4 flex flex-wrap gap-x-4 gap-y-3 sm:mt-6 sm:gap-x-6 xl:mt-[170px] xl:flex-col xl:flex-nowrap xl:gap-6"
          role="list"
        >
          {processItems.map((item) => {
            const isActive = active === item.id
            return (
              <li key={item.id}>
                <button
                  className="flex w-auto items-center gap-2 rounded-lg text-left transition-colors sm:gap-3 xl:w-full"
                  onClick={() => setActive(item.id)}
                >
                  <span
                    className={`shrink-0 font-sans text-[14px] font-medium not-italic leading-normal xl:tracking-[-1px] tabular-nums transition-colors sm:text-[18px] xl:text-[24px] ${
                      isActive ? 'text-[#D4575B]' : 'text-[rgba(28,25,23,0.30)]'
                    }`}
                  >
                    {item.num}
                  </span>
                  <span
                    className={`font-sans text-[14px] font-normal not-italic leading-normal xl:tracking-[-1px] transition-colors sm:text-[18px] xl:text-[24px] ${
                      isActive ? 'text-[#1C1917]' : 'text-[rgba(28,25,23,0.60)]'
                    }`}
                  >
                    {item.label}
                  </span>
                </button>
              </li>
            )
          })}
        </ul>
      </motion.div>

      {/* Center: image */}
      <motion.div
        className="h-[220px] w-full overflow-hidden sm:h-[320px] xl:h-[580px] xl:w-[418px]"
        initial={{ opacity: 0, scale: 0.96 }}
        whileInView={{ opacity: 1, scale: 1 }}
        viewport={{ once: true }}
        transition={{ duration: 0.6, delay: 0.1, ease: 'easeOut' }}
      >
        <img src={detail.image} alt={detail.title} className="h-full w-full object-cover" />
      </motion.div>

      {/* Right: detail panel */}
      <motion.div
        className="flex w-full flex-col justify-end bg-[#FAFAFA] px-4 py-6 sm:px-6 sm:py-8 xl:h-[580px] xl:w-[421px] xl:px-10 xl:py-[49px]"
        initial={{ opacity: 0, x: 16 }}
        whileInView={{ opacity: 1, x: 0 }}
        viewport={{ once: true }}
        transition={{ duration: 0.5, delay: 0.1, ease: 'easeOut' }}
      >
        <AnimatePresence mode="wait">
          <motion.div
            key={active}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.25, ease: 'easeOut' }}
          >
            <h3 className="font-sans text-[20px] font-medium not-italic leading-normal tracking-[-1px] text-[#1C1917] sm:text-[24px] xl:text-[28px]">
              {detail.title}
            </h3>
            <p className="mt-2 font-sans text-[14px] font-normal not-italic leading-[150%] tracking-[-0.253px] text-[#7E7E7E] sm:mt-3 sm:text-[17px] xl:text-[23px]">
              {detail.description}
            </p>
            {/* <div className="mt-4 sm:mt-6">
                  <button
                    className="flex items-center justify-center gap-[10px] rounded-[10px] border border-[rgba(41,41,41,0.09)] bg-[#302F2F] px-4 py-3 text-center font-sans text-[14px] font-normal not-italic leading-normal text-white transition-colors hover:bg-[#3d3c3c] sm:p-4 sm:text-[16px] xl:text-[18px]"
                    style={{ leadingTrim: 'both', textEdge: 'cap' } as CSSProperties}
                  >
                    See More Projects
                  </button>
                </div> */}
          </motion.div>
        </AnimatePresence>
      </motion.div>
    </div>
  )
}

export function ExecutionSection() {
  return (
    <section
      id="execution"
      className="bg-white pt-16 sm:pt-24 xl:pt-[200px]"
      aria-labelledby="execution-heading"
    >
      <div className="container-site">
        <div className="mb-10 flex flex-col items-center justify-center">
          <span
            className="block text-center font-sans text-[18px] font-bold not-italic leading-[17.009px] tracking-[0.061px] text-[#D4575B] uppercase"
            style={{ leadingTrim: 'both', textEdge: 'cap' } as CSSProperties}
          >
            Our Process
          </span>
          <h2
            id="execution-heading"
            className="mx-auto mt-10 w-full max-w-[1002px] text-center font-sans text-[28px] font-medium not-italic leading-[36px] tracking-[-1px] text-[#2D2D2D] sm:text-[36px] sm:leading-[44px] xl:text-[48px] xl:leading-[55px]"
          >
            Built Around Execution.
          </h2>
          <p className="mx-auto mt-2 w-full max-w-[1100px] text-center font-sans text-[22px] font-medium not-italic leading-[30px] tracking-[-1px] text-[#979AC0] sm:text-[30px] sm:leading-[38px] xl:text-[48px] xl:leading-[55px]">
            Processes designed to support scalable product development and long-term technology
            operations.
          </p>
        </div>
        <BackofficeExecutionGrid />
      </div>
    </section>
  )
}
